import {Template} from "../../../../components/template/template.jsx";
import React, {useMemo, useRef, useState, useCallback, useEffect} from "react";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import Button from "../../../../components/button/button.jsx";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {AgGridReact} from "ag-grid-react";
import {useQuery} from "@tanstack/react-query";
import AbsenceService from "../../../../services/web/absence-service.js";
import AbsenceTypeService from "../../../../services/web/absence-type-service.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import {createPathModel} from "../../../../utils/create-path-model.js";
import PeriodInput from "../../../../components/input/period-input.jsx";
import {useGeneralInput, useGeneralInputSpecialCase} from "../../../../hooks/use-general-input.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../utils/ag-grid-columns-utils.js";
import {format} from "date-fns";
import {useFormNavigation} from "../../../../hooks/use-form-navigation.js";

const initialFilter = {
    name: "",
    namewar: "",
    reason: null,
    start_date: null,
    end_date: null
}

const pathModel = createPathModel({base: "absences"})
const AbsencesList = () => {
    const [filter, setFilter] = useState(initialFilter);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);
    const { onRowClicked, openForm } = useFormNavigation(pathModel);
    const handleInput = useGeneralInput(setFilter);
    const handleSpecialInput = useGeneralInputSpecialCase(setFilter);

    const columnDefs = useMemo(() => [
        {field: "id", hide: true},
        {field: "name", headerName: "Nome", flex: 1, filter: "agTextColumnFilter",
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {field: "namewar", headerName: "Nome de guerra", flex: 1, filter: "agTextColumnFilter",
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        {field: "reason", headerName: "Motivo", flex: 1, filter: "agTextColumnFilter",
            valueFormatter: params => params.value},
        {field: "start_date", headerName: "Data inicial", flex: 1, filter: "agDateColumnFilter", cellDataType: "date", sortable: true,
            comparator: (a, b) => {
                const timeA = a?.getTime();
                const timeB = b?.getTime();

                if (timeA === timeB) return 0;
                if (timeA == null) return 1;
                if (timeB == null) return -1;

                return timeA - timeB;
            },
            valueFormatter: (params) => {
                if (!params.value) return "";
                return new Date(params.value).toLocaleDateString("pt-BR");
            },
            filterParams: {
                inRangeInclusive: true,
                comparator: (filterDate, cellValue) => {
                    if (!cellValue) return -1;

                    const cellDate = new Date(cellValue);
                    cellDate.setHours(0, 0, 0, 0);

                    if (cellDate < filterDate) return -1;
                    if (cellDate > filterDate) return 1;
                    return 0;
                }
            }},
        {field: "end_date", headerName: "Data final", flex: 1, filter: "agDateColumnFilter", cellDataType: "date", sortable: true,
            comparator: (a, b) => {
                const timeA = a?.getTime();
                const timeB = b?.getTime();

                if (timeA === timeB) return 0;
                if (timeA == null) return 1;
                if (timeB == null) return -1;

                return timeA - timeB;
            },
            valueFormatter: (params) => {
                if (!params.value) return "";
                return new Date(params.value).toLocaleDateString("pt-BR");
            },
            filterParams: {
                inRangeInclusive: true,
                comparator: (filterDate, cellValue) => {
                    if (!cellValue) return -1;

                    const cellDate = new Date(cellValue);
                    cellDate.setHours(0, 0, 0, 0);

                    if (cellDate < filterDate) return -1;
                    if (cellDate > filterDate) return 1;
                    return 0;
                }
            }},
    ], []);

    const { data: reasons = [] } = useQuery({
        queryKey: ["absence-types"],
        queryFn: async () => (await AbsenceTypeService.list()).map(({name}) => ({value: name, label: name}))
    });

    const { data: absences = [], isLoading, refetch: refetchList } = useQuery({
        queryKey: ['absences'],
        queryFn: async () => {
            const { data } = await AbsenceService.list();
            return data;
        },
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: "always"
    });

    const rowData = useMemo(() => {
        if (!absences) return [];

        const parseDate = (value) => {
            if (!value) return null;

            const [year, month, day] = value.split("T")[0].split("-");
            const date = new Date(year, month - 1, day);

            return Number.isNaN(date.getTime()) ? null : date;
        };

        return absences.map(item => {
            return ({
                ...item,
                start_date: parseDate(item.start_date),
                end_date: parseDate(item.end_date)
            });
        });
    }, [absences]);

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        if (filter.start_date && filter.end_date) {
            const dateRangeFilter = {
                filterType: "date",
                type: "inRange",
                dateFrom: format(filter.start_date, 'yyyy-MM-dd'),
                dateTo: format(filter.end_date, 'yyyy-MM-dd'),
            };

            filterModel["start_date"] = dateRangeFilter;
            filterModel["end_date"] = dateRangeFilter;
        }

        Object.entries(filter).forEach(([field, value]) => {
            if (!value) return;
            if (field === 'start_date' || field === 'end_date') return;

            if(field === 'reason') {
                filterModel[field] = {
                    filterType: "text",
                    type: "equals",
                    filter: value
                };
            } else {
                filterModel[field] = {
                    filterType: "text",
                    type: "contains",
                    filter: value
                };
            }
        });

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>
                    Ausências
                </Template.Title>
                <Button onClick={openForm}>
                    <FaPlus/><span className="font-bold">Adicionar ausência</span>
                </Button>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-9 gap-4">
                            <div className="col-span-2">
                                <InputForm id="name" name="name" value={filter.name} onChange={handleInput} label="Nome"/>
                            </div>

                            <div className="col-span-2">
                                <InputForm id="namewar" name="namewar" value={filter.namewar} onChange={handleInput} label="Nome de guerra"/>
                            </div>

                            <div className="col-span-2">
                                <SelectComponent
                                    id="reason"
                                    value={reasons.find(s => s.value === filter.reason) ?? null}
                                    options={reasons}
                                    label={"Motivo"}
                                    onChange={(selected) => handleSpecialInput("reason", selected?.value)}
                                />
                            </div>

                            <div className="col-span-3 flex gap-4">
                                <div className="flex-1">
                                    <PeriodInput
                                      startDateId={"start_date"}
                                      endDateId={"end_date"}
                                      startDate={filter.start_date}
                                      endDate={filter.end_date}
                                      setFilter={setFilter}
                                    />
                                </div>

                                <div className="flex gap-2">
                                    <Button target="primary-outline" onClick={clearFilter}><FaEraser/></Button>
                                    <Button onClick={search}><FaSearch/></Button>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card className="mt-2">
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                ref={gridRef}
                                rowData={rowData}
                                columnDefs={columnDefs}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                pagination
                                onRowClicked={onRowClicked}
                                paginationPageSize={50}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}

export default AbsencesList;